import { Link, useParams } from "react-router-dom";
import productsQueries from "../../../requests/queries/ProductsQueries";
import { useState, useEffect } from "react";
import "./Products.css";
import ImagePicker from "../projects/editor/components/picker/ImagePicker";

const DAYS_OF_WEEK = [
    { value: 0, label: "Dim" },
    { value: 1, label: "Lun" },
    { value: 2, label: "Mar" },
    { value: 3, label: "Mer" },
    { value: 4, label: "Jeu" },
    { value: 5, label: "Ven" },
    { value: 6, label: "Sam" },
];

const NUTRITION_SCORES = ["A", "B", "C", "D", "E"];

export default function Products() {
    const { id: projectId } = useParams();
    
    // États principaux
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState("all");
    
    // États modaux
    const [showProductModal, setShowProductModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    
    // État formulaire produit
    const [productForm, setProductForm] = useState({
        name: "",
        description: "",
        allergens: [],
        ingredients: [],
        nutriments: [],
        nutrition_score: "",
        available_from: "",
        available_to: "",
        available_days: [0, 1, 2, 3, 4, 5, 6],
        is_active: true,
        tags: [],
        highlight: false,
        newcactus_menu_item_id: '',
    });
    
    // État formulaire catégorie
    const [categoryForm, setCategoryForm] = useState({
        name: "",
        description: "",
    });
    
    // État pour ajouter des prix
    const [newPrice, setNewPrice] = useState("");
    const [productPrices, setProductPrices] = useState([]);
    const [defaultPriceId, setDefaultPriceId] = useState(null);
    
    // État pour ajouter des images
    const [newImageUrl, setNewImageUrl] = useState("");
    const [productImages, setProductImages] = useState([]);
    const [defaultImageId, setDefaultImageId] = useState(null);
    
    // État pour les catégories du produit
    const [productCategories, setProductCategories] = useState([]);
    
    // État pour les allergènes
    const [allAllergens, setAllAllergens] = useState([]);
    const [productAllergens, setProductAllergens] = useState([]);
    
    // État pour inputs de tags
    const [tagInputs, setTagInputs] = useState({
        ingredients: "",
        tags: "",
    });

    useEffect(() => {
        loadData();
    }, [projectId]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [productsData, categoriesData, allergensData] = await Promise.all([
                productsQueries.getAll(projectId),
                productsQueries.getAllCategories(projectId),
                productsQueries.getAllAllergens()
            ]);
            setProducts(productsData || []);
            setCategories(categoriesData || []);
            setAllAllergens(allergensData || []);
        } catch (error) {
            console.error("Erreur lors du chargement:", error);
        } finally {
        setIsLoading(false);
        }
    };

    // ========================================
    // CRUD PRODUCTS
    // ========================================

    const handleCreateProduct = () => {
        setEditingProduct(null);
        setProductForm({
            name: "",
            description: "",
            ingredients: [],
            nutriments: [],
            nutrition_score: "",
            available_from: "",
            available_to: "",
            available_days: [0, 1, 2, 3, 4, 5, 6],
            is_active: true,
            tags: [],
            highlight: false,
            newcactus_menu_item_id: '',
        });
        setProductPrices([]);
        setProductImages([]);
        setDefaultPriceId(null);
        setDefaultImageId(null);
        setProductCategories([]);
        setProductAllergens([]);
        setTagInputs({ ingredients: "", tags: "" });
        setShowProductModal(true);
    };

    const handleEditProduct = (product) => {
        setEditingProduct(product);
        setProductForm({
            name: product.name || "",
            description: product.description || "",
            ingredients: product.ingredients || [],
            nutriments: product.nutriments || [],
            nutrition_score: product.nutrition_score || "",
            available_from: product.available_from || "",
            available_to: product.available_to || "",
            available_days: product.available_days || [0, 1, 2, 3, 4, 5, 6],
            is_active: product.is_active ?? true,
            tags: product.tags || [],
            highlight: product.highlight || false,
            newcactus_menu_item_id: product.newcactus_menu_item_id ?? '',
        });
        setProductPrices(product.prices || []);
        setProductImages(product.images || []);
        setDefaultPriceId(product.default_price_id || null);
        setDefaultImageId(product.default_image_id || null);
        setProductCategories(product.categories || []);
        // Les allergènes sont maintenant des objets, pas des strings
        setProductAllergens(product.allergens || []);
        setTagInputs({ ingredients: "", tags: "" });
        setShowProductModal(true);
    };

    const handleSaveProduct = async () => {
        try {
            const productData = {
                ...productForm,
                project_id: projectId,
                newcactus_menu_item_id: productForm.newcactus_menu_item_id
                    ? Number(productForm.newcactus_menu_item_id)
                    : null,
            };

            if (editingProduct) {
                await productsQueries.update(editingProduct.id, productData);
            } else {
                const newProduct = await productsQueries.create(productData);
                
                // Ajouter les prix
                for (const price of productPrices) {
                    if (!price.id) {
                        await productsQueries.createPrice(newProduct.id, price.price);
                    }
                }
                
                // Ajouter les images
                for (const image of productImages) {
                    if (!image.id) {
                        await productsQueries.createImage(newProduct.id, image.image_url);
                    }
                }

                // Synchroniser les allergènes
                if (productAllergens.length > 0) {
                    const allergenIds = productAllergens.map(a => a.id);
                    await productsQueries.syncProductAllergens(newProduct.id, allergenIds);
                }

                // Lier les catégories
                for (const category of productCategories) {
                    await productsQueries.linkProductToCategory(category.id, newProduct.id);
                }
            }

            setShowProductModal(false);
            loadData();
        } catch (error) {
            console.error("Erreur lors de la sauvegarde:", error);
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;
        
        try {
            await productsQueries.delete(productId);
            loadData();
        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
        }
    };

    // ========================================
    // CRUD CATEGORIES
    // ========================================

    const handleCreateCategory = () => {
        setEditingCategory(null);
        setCategoryForm({ name: "", description: "" });
        setShowCategoryModal(true);
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setCategoryForm({
            name: category.name || "",
            description: category.description || "",
        });
        setShowCategoryModal(true);
    };

    const handleSaveCategory = async () => {
        try {
            if (editingCategory) {
                await productsQueries.updateCategory(editingCategory.id, categoryForm.name, categoryForm.description);
            } else {
                await productsQueries.createCategory(categoryForm.name, categoryForm.description, projectId);
            }
            setShowCategoryModal(false);
            loadData();
        } catch (error) {
            console.error("Erreur lors de la sauvegarde:", error);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette catégorie ?")) return;
        
        try {
            await productsQueries.deleteCategory(categoryId);
            loadData();
        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
        }
    };

    // ========================================
    // HELPERS
    // ========================================

    const handleTagKeyDown = (e, field) => {
        if (e.key === "Enter" && tagInputs[field].trim()) {
            e.preventDefault();
            setProductForm(prev => ({
                ...prev,
                [field]: [...prev[field], tagInputs[field].trim()]
            }));
            setTagInputs(prev => ({ ...prev, [field]: "" }));
        }
    };

    const removeTag = (field, index) => {
        setProductForm(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    const toggleDay = (day) => {
        setProductForm(prev => {
            const days = prev.available_days || [];
            if (days.includes(day)) {
                return { ...prev, available_days: days.filter(d => d !== day) };
            } else {
                return { ...prev, available_days: [...days, day].sort() };
            }
        });
    };

    const addPrice = async () => {
        if (newPrice && !isNaN(parseFloat(newPrice))) {
            const priceValue = parseFloat(newPrice);
            
            if (editingProduct) {
                // Produit existant : sauvegarder immédiatement en BDD
                try {
                    const newPriceData = await productsQueries.createPrice(editingProduct.id, priceValue);
                    setProductPrices(prev => [...prev, newPriceData]);
                    
                    // Si c'est le premier prix, le définir par défaut
                    if (productPrices.length === 0) {
                        await productsQueries.setDefaultPrice(editingProduct.id, newPriceData.id);
                        setDefaultPriceId(newPriceData.id);
                    }
                } catch (error) {
                    console.error("Erreur lors de l'ajout du prix:", error);
                }
            } else {
                // Nouveau produit : ajouter localement
                setProductPrices(prev => [...prev, { price: priceValue }]);
            }
            
            setNewPrice("");
        }
    };

    const removePrice = (index) => {
        setProductPrices(prev => prev.filter((_, i) => i !== index));
    };

    const addImage = () => {
        if (newImageUrl.trim()) {
            setProductImages(prev => [...prev, { image_url: newImageUrl.trim() }]);
            setNewImageUrl("");
        }
    };

    const removeImage = (index) => {
        setProductImages(prev => prev.filter((_, i) => i !== index));
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
    };

    const getDefaultPrice = (product) => {
        if (!product.prices || product.prices.length === 0) return null;
        if (product.default_price_id) {
            const defaultPrice = product.prices.find(p => p.id === product.default_price_id);
            if (defaultPrice) return defaultPrice;
        }
        return product.prices[0];
    };

    const getDefaultImage = (product) => {
        if (!product.images || product.images.length === 0) return null;
        if (product.default_image_id) {
            const defaultImage = product.images.find(img => img.id === product.default_image_id);
            if (defaultImage) return defaultImage;
        }
        return product.images[0];
    };

    const handleDeleteImage = async (imageId) => {
        try {
            await productsQueries.deleteImage(imageId);
            setProductImages(prev => prev.filter(img => img.id !== imageId));
            if (defaultImageId === imageId) {
                setDefaultImageId(null);
            }
        } catch (error) {
            console.error("Erreur lors de la suppression de l'image:", error);
        }
    };

    const handleSetDefaultImage = async (imageId) => {
        if (!editingProduct) return;
        try {
            await productsQueries.setDefaultImage(editingProduct.id, imageId);
            setDefaultImageId(imageId);
        } catch (error) {
            console.error("Erreur lors de la définition de l'image par défaut:", error);
        }
    };

    const handleToggleCategory = async (category) => {
        if (!editingProduct) {
            // Nouveau produit : gérer localement
            const isLinked = productCategories.some(c => c.id === category.id);
            if (isLinked) {
                setProductCategories(prev => prev.filter(c => c.id !== category.id));
            } else {
                setProductCategories(prev => [...prev, category]);
            }
            return;
        }

        // Produit existant : appeler l'API
        const isLinked = productCategories.some(c => c.id === category.id);
        try {
            if (isLinked) {
                await productsQueries.unlinkProductFromCategory(category.id, editingProduct.id);
                setProductCategories(prev => prev.filter(c => c.id !== category.id));
            } else {
                await productsQueries.linkProductToCategory(category.id, editingProduct.id);
                setProductCategories(prev => [...prev, category]);
            }
        } catch (error) {
            console.error("Erreur lors de la modification des catégories:", error);
        }
    };

    const handleToggleAllergen = async (allergen) => {
        const isLinked = productAllergens.some(a => a.id === allergen.id);
        
        if (!editingProduct) {
            // Nouveau produit : gérer localement
            if (isLinked) {
                setProductAllergens(prev => prev.filter(a => a.id !== allergen.id));
            } else {
                setProductAllergens(prev => [...prev, allergen]);
            }
            return;
        }

        // Produit existant : appeler l'API
        try {
            if (isLinked) {
                await productsQueries.unlinkAllergenFromProduct(editingProduct.id, allergen.id);
                setProductAllergens(prev => prev.filter(a => a.id !== allergen.id));
            } else {
                await productsQueries.linkAllergenToProduct(editingProduct.id, allergen.id);
                setProductAllergens(prev => [...prev, allergen]);
            }
        } catch (error) {
            console.error("Erreur lors de la modification des allergènes:", error);
        }
    };

    const formatAvailability = (product) => {
        const parts = [];
        
        if (product.available_from && product.available_to) {
            parts.push(`${product.available_from.slice(0, 5)} - ${product.available_to.slice(0, 5)}`);
        }
        
        if (product.available_days && product.available_days.length < 7) {
            const dayNames = product.available_days.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).join(", ");
            parts.push(dayNames);
        }
        
        return parts.length > 0 ? parts.join(" • ") : "Toujours disponible";
    };

    // Filtrage des produits
    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             product.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = activeCategory === "all" || 
                               product.categories?.some(c => c.id === activeCategory);
        return matchesSearch && matchesCategory;
    });

    // ========================================
    // RENDER
    // ========================================

    if (isLoading) {
        return (
            <div className="products-page">
                <div className="products-container">
                <div className="products-loading">
                    <div className="loading-spinner"></div>
                    <p>Chargement des produits...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="products-page">
            <div className="products-container">
                {/* Lien retour */}
                <Link to={`/project/${projectId}`} className="products-back">
                    ← Retour au projet
                </Link>

                {/* Header */}
                <header className="products-header">
                    <div className="products-header-top">
                        <div className="products-title-group">
                            <div className="products-title-icon">🍽️</div>
                            <div>
                                <h1 className="products-title">Gestion des produits</h1>
                                <p className="products-subtitle">{products.length} produit{products.length > 1 ? 's' : ''} • {categories.length} catégorie{categories.length > 1 ? 's' : ''}</p>
                            </div>
                        </div>
                        <div className="products-header-actions">
                            <div className="products-search">
                                <span className="products-search-icon">🔍</span>
                                <input
                                    type="text"
                                    className="products-search-input"
                                    placeholder="Rechercher un produit..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <button className="products-btn products-btn-primary" onClick={handleCreateProduct}>
                                ➕ Nouveau produit
                            </button>
                        </div>
                    </div>
                </header>

                {/* Tabs catégories */}
                <div className="products-tabs">
                    <button
                        className={`products-tab ${activeCategory === "all" ? "active" : ""}`}
                        onClick={() => setActiveCategory("all")}
                    >
                        🍴 Tous
                        <span className="products-tab-count">{products.length}</span>
                    </button>
                    {categories.map(category => (
                        <button
                            key={category.id}
                            className={`products-tab ${activeCategory === category.id ? "active" : ""}`}
                            onClick={() => setActiveCategory(category.id)}
                            onDoubleClick={() => handleEditCategory(category)}
                        >
                            {category.name}
                            <span className="products-tab-count">
                                {products.filter(p => p.categories?.some(c => c.id === category.id)).length}
                            </span>
                        </button>
                    ))}
                    <button className="products-tab products-tab-add" onClick={handleCreateCategory}>
                        ➕ Catégorie
                    </button>
                </div>

                {/* Liste des produits */}
                {filteredProducts.length === 0 ? (
                    <div className="products-empty">
                        <div className="products-empty-icon">🍽️</div>
                        <h2 className="products-empty-title">Aucun produit</h2>
                        <p className="products-empty-text">
                            {searchQuery 
                                ? "Aucun produit ne correspond à votre recherche."
                                : "Commencez par ajouter votre premier produit pour créer votre menu."}
                        </p>
                        {!searchQuery && (
                            <button className="products-btn products-btn-primary" onClick={handleCreateProduct}>
                                ➕ Ajouter un produit
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="products-grid">
                        {filteredProducts.map((product) => (
                            <div key={product.id} className="product-card">
                                {/* Image */}
                                <div className="product-card-image">
                                    {getDefaultImage(product) ? (
                                        <img src={getDefaultImage(product).image_url} alt={product.name} />
                                    ) : (
                                        <div className="product-card-image-placeholder">🍽️</div>
                                    )}
                                    
                                    {/* Badges */}
                                    <div className="product-card-badges">
                                        {product.highlight && (
                                            <span className="product-badge product-badge-highlight">⭐ Vedette</span>
                                        )}
                                        <span className={`product-badge ${product.is_active ? 'product-badge-active' : 'product-badge-inactive'}`}>
                                            {product.is_active ? '✓ Actif' : '✗ Inactif'}
                                        </span>
                                        {product.nutrition_score && (
                                            <span className="product-badge product-badge-score">
                                                Nutri-Score {product.nutrition_score}
                                            </span>
                                        )}
                                    </div>

                                    {/* Menu */}
                                    <div className="product-card-menu">
                                        <button className="product-card-menu-btn" onClick={() => handleEditProduct(product)}>
                                            ✏️
                                        </button>
                                    </div>
                                </div>

                                {/* Contenu */}
                                <div className="product-card-content">
                                    <div className="product-card-header">
                                        <h3 className="product-card-title">{product.name}</h3>
                                        {getDefaultPrice(product) && (
                                            <span className="product-card-price">
                                                {formatPrice(getDefaultPrice(product).price)}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <p className="product-card-description">
                                        {product.description || "Aucune description"}
                                    </p>

                                    {(product.tags?.length > 0 || product.allergens?.length > 0) && (
                                        <div className="product-card-tags">
                                            {product.tags?.slice(0, 3).map((tag, i) => (
                                                <span key={i} className="product-tag">{tag}</span>
                                            ))}
                                            {product.allergens?.slice(0, 3).map((allergen, i) => (
                                                <span key={i} className="product-tag product-tag-allergen" title={allergen.description}>
                                                    {allergen.icon} {allergen.name}
                                                </span>
                                            ))}
                                            {product.allergens?.length > 3 && (
                                                <span className="product-tag product-tag-allergen">
                                                    +{product.allergens.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Disponibilité */}
                                    <div className="product-card-availability">
                                        <span className="product-card-availability-icon">🕐</span>
                                        <span className="product-card-availability-text">
                                            {formatAvailability(product)}
                                        </span>
                                    </div>

                                    {/* Actions */}
                                    <div className="product-card-actions">
                                        <button 
                                            className="product-card-btn product-card-btn-edit"
                                            onClick={() => handleEditProduct(product)}
                                        >
                                            ✏️ Modifier
                                        </button>
                                        <button 
                                            className="product-card-btn product-card-btn-delete"
                                            onClick={() => handleDeleteProduct(product.id)}
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ========================================
                    MODAL PRODUIT
                   ======================================== */}
                {showProductModal && (
                    <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title">
                                    {editingProduct ? "✏️ Modifier le produit" : "➕ Nouveau produit"}
                                </h2>
                                <button className="modal-close" onClick={() => setShowProductModal(false)}>
                                    ✕
                                </button>
                            </div>
                            
                            <div className="modal-body">
                                {/* Infos de base */}
                                <div className="form-group">
                                    <label className="form-label">Nom du produit *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Ex: Pizza Margherita"
                                        value={productForm.name}
                                        onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-textarea"
                                        placeholder="Description du produit..."
                                        value={productForm.description}
                                        onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">ID plat New Cactus (menu_item_id)</label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        placeholder="Ex: 87"
                                        min="1"
                                        value={productForm.newcactus_menu_item_id}
                                        onChange={(e) =>
                                            setProductForm((prev) => ({
                                                ...prev,
                                                newcactus_menu_item_id: e.target.value,
                                            }))
                                        }
                                    />
                                    <p className="form-hint">
                                        Lien entre ce produit CMS et le plat New Cactus (obligatoire si paiement New Cactus).
                                    </p>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Nutri-Score</label>
                                        <select
                                            className="form-select"
                                            value={productForm.nutrition_score}
                                            onChange={(e) => setProductForm(prev => ({ ...prev, nutrition_score: e.target.value }))}
                                        >
                                            <option value="">Non renseigné</option>
                                            {NUTRITION_SCORES.map(score => (
                                                <option key={score} value={score}>Nutri-Score {score}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Toggles */}
                                <div className="form-row">
                                    <div 
                                        className={`form-toggle ${productForm.is_active ? 'active' : ''}`}
                                        onClick={() => setProductForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                                    >
                                        <div className="form-toggle-text">
                                            <span className="form-toggle-label">Produit actif</span>
                                            <span className="form-toggle-description">Visible dans le menu</span>
                                        </div>
                                        <div className="form-toggle-switch"></div>
                                    </div>
                                    
                                    <div 
                                        className={`form-toggle ${productForm.highlight ? 'active' : ''}`}
                                        onClick={() => setProductForm(prev => ({ ...prev, highlight: !prev.highlight }))}
                                    >
                                        <div className="form-toggle-text">
                                            <span className="form-toggle-label">Produit vedette</span>
                                            <span className="form-toggle-description">Mis en avant</span>
                                        </div>
                                        <div className="form-toggle-switch"></div>
                                    </div>
                                </div>

                                {/* Catégories */}
                                {categories.length > 0 && (
                                    <>
                                        <h3 className="form-section-title">📂 Catégories</h3>
                                        <div className="form-categories">
                                            {categories.map(category => {
                                                const isSelected = productCategories.some(c => c.id === category.id);
                                                return (
                                                    <label 
                                                        key={category.id} 
                                                        className={`form-category-item ${isSelected ? 'active' : ''}`}
                                                        onClick={() => handleToggleCategory(category)}
                                                    >
                                                        <span className="form-category-checkbox">
                                                            {isSelected ? '✓' : ''}
                                                        </span>
                                                        <span className="form-category-name">{category.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}

                                {/* Disponibilité */}
                                <h3 className="form-section-title">🕐 Disponibilité</h3>
                                
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Heure de début</label>
                                        <input
                                            type="time"
                                            className="form-input"
                                            value={productForm.available_from}
                                            onChange={(e) => setProductForm(prev => ({ ...prev, available_from: e.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Heure de fin</label>
                                        <input
                                            type="time"
                                            className="form-input"
                                            value={productForm.available_to}
                                            onChange={(e) => setProductForm(prev => ({ ...prev, available_to: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Jours de disponibilité</label>
                                    <div className="form-checkbox-group">
                                        {DAYS_OF_WEEK.map(day => (
                                            <label 
                                                key={day.value} 
                                                className={`form-checkbox-label ${productForm.available_days?.includes(day.value) ? 'active' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox"
                                                    checked={productForm.available_days?.includes(day.value)}
                                                    onChange={() => toggleDay(day.value)}
                                                />
                                                {day.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Prix */}
                                <h3 className="form-section-title">💰 Prix</h3>
                                
                                <div className="form-prices">
                                    {productPrices.map((price) => {
                                        const isDefault = price.id === defaultPriceId || 
                                            (!defaultPriceId && productPrices.indexOf(price) === 0 && price.id);
                                        return (
                                            <div key={price.id || price.price} className={`form-price-item ${isDefault ? 'is-default' : ''}`}>
                                                <span className="form-price-value">{formatPrice(price.price)}</span>
                                                {isDefault && <span className="form-price-default">Par défaut</span>}
                                                <span className="form-price-label"></span>
                                                {price.id && !isDefault && editingProduct && (
                                                    <button 
                                                        className="form-price-set-default" 
                                                        onClick={async () => {
                                                            await productsQueries.setDefaultPrice(editingProduct.id, price.id);
                                                            setDefaultPriceId(price.id);
                                                        }}
                                                    >
                                                        Définir par défaut
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    <div className="form-price-add">
                                        <input
                                            type="number"
                                            className="form-price-add-input"
                                            placeholder="Prix (€)"
                                            step="0.01"
                                            value={newPrice}
                                            onChange={(e) => setNewPrice(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addPrice();
                                                }
                                            }}
                                        />
                                        <button type="button" className="form-price-add-btn" onClick={addPrice}>
                                            Ajouter
                                        </button>
                                    </div>
                                </div>

                                {/* Images */}
                                <h3 className="form-section-title">🖼️ Images</h3>
                                
                                <div className="form-images">
                                    {productImages.map((image) => {
                                        const isDefault = image.id === defaultImageId || 
                                            (!defaultImageId && productImages.indexOf(image) === 0 && image.id);
                                        return (
                                            <div key={image.id || image.image_url} className={`form-image-item ${isDefault ? 'is-default' : ''}`}>
                                                <img src={image.image_url} alt="Image produit" />
                                                {isDefault && <span className="form-image-default-badge">★ Défaut</span>}
                                                <div className="form-image-actions">
                                                    {image.id && !isDefault && editingProduct && (
                                                        <button 
                                                            className="form-image-set-default" 
                                                            onClick={() => handleSetDefaultImage(image.id)}
                                                            title="Définir par défaut"
                                                        >
                                                            ★
                                                        </button>
                                                    )}
                                                    {image.id && (
                                                        <button 
                                                            className="form-image-remove" 
                                                            onClick={() => handleDeleteImage(image.id)}
                                                            title="Supprimer"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
            </div>
        );
                                    })}
                                    <div className="form-image-add-picker">
                                        <ImagePicker
                                            value={null}
                                            onChange={async (value) => {
                                                if (value && typeof value === 'string') {
                                                    if (editingProduct) {
                                                        // Produit existant : sauvegarder immédiatement en BDD
                                                        try {
                                                            const newImage = await productsQueries.createImage(editingProduct.id, value);
                                                            setProductImages(prev => [...prev, newImage]);
                                                        } catch (error) {
                                                            console.error("Erreur lors de l'ajout de l'image:", error);
                                                        }
                                                    } else {
                                                        // Nouveau produit : ajouter localement (sera sauvé à la création)
                                                        setProductImages(prev => [...prev, { image_url: value }]);
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Allergènes - 14 allergènes obligatoires */}
                                <h3 className="form-section-title">⚠️ Allergènes (réglementation européenne)</h3>
                                
                                <div className="form-allergens">
                                    {allAllergens.map(allergen => {
                                        const isSelected = productAllergens.some(a => a.id === allergen.id);
                                        return (
                                            <label 
                                                key={allergen.id} 
                                                className={`form-allergen-item ${isSelected ? 'active' : ''}`}
                                                onClick={() => handleToggleAllergen(allergen)}
                                                title={allergen.description}
                                            >
                                                <span className="form-allergen-checkbox">
                                                    {isSelected ? '✓' : ''}
                                                </span>
                                                <span className="form-allergen-icon">{allergen.icon}</span>
                                                <span className="form-allergen-name">{allergen.name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {productAllergens.length > 0 && (
                                    <p className="form-allergens-count">
                                        {productAllergens.length} allergène{productAllergens.length > 1 ? 's' : ''} sélectionné{productAllergens.length > 1 ? 's' : ''}
                                    </p>
                                )}

                                {/* Ingrédients */}
                                <h3 className="form-section-title">🥗 Ingrédients</h3>
                                
                                <div className="form-group">
                                    <div className="form-tags">
                                        {productForm.ingredients?.map((ingredient, index) => (
                                            <span key={index} className="form-tag">
                                                {ingredient}
                                                <button className="form-tag-remove" onClick={() => removeTag('ingredients', index)}>
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                        <input
                                            type="text"
                                            className="form-tags-input"
                                            placeholder="Ajouter un ingrédient..."
                                            value={tagInputs.ingredients}
                                            onChange={(e) => setTagInputs(prev => ({ ...prev, ingredients: e.target.value }))}
                                            onKeyDown={(e) => handleTagKeyDown(e, 'ingredients')}
                                        />
                                    </div>
                                </div>

                                {/* Tags */}
                                <h3 className="form-section-title">🏷️ Tags</h3>
                                
                                <div className="form-group">
                                    <div className="form-tags">
                                        {productForm.tags?.map((tag, index) => (
                                            <span key={index} className="form-tag">
                                                {tag}
                                                <button className="form-tag-remove" onClick={() => removeTag('tags', index)}>
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                        <input
                                            type="text"
                                            className="form-tags-input"
                                            placeholder="Ajouter un tag..."
                                            value={tagInputs.tags}
                                            onChange={(e) => setTagInputs(prev => ({ ...prev, tags: e.target.value }))}
                                            onKeyDown={(e) => handleTagKeyDown(e, 'tags')}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button 
                                    className="products-btn products-btn-secondary" 
                                    onClick={() => setShowProductModal(false)}
                                >
                                    Annuler
                                </button>
                                <button 
                                    className="products-btn products-btn-primary" 
                                    onClick={handleSaveProduct}
                                    disabled={!productForm.name.trim()}
                                >
                                    {editingProduct ? "💾 Enregistrer" : "➕ Créer le produit"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========================================
                    MODAL CATÉGORIE
                   ======================================== */}
                {showCategoryModal && (
                    <div className="modal-overlay category-modal" onClick={() => setShowCategoryModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="modal-title">
                                    {editingCategory ? "✏️ Modifier la catégorie" : "➕ Nouvelle catégorie"}
                                </h2>
                                <button className="modal-close" onClick={() => setShowCategoryModal(false)}>
                                    ✕
                                </button>
                            </div>
                            
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Nom de la catégorie *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Ex: Entrées, Plats, Desserts..."
                                        value={categoryForm.name}
                                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Description <span className="form-label-optional">(optionnel)</span>
                                    </label>
                                    <textarea
                                        className="form-textarea"
                                        placeholder="Description de la catégorie..."
                                        value={categoryForm.description}
                                        onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </div>

                                {editingCategory && (
                                    <button 
                                        className="products-btn products-btn-secondary" 
                                        style={{ color: 'var(--pr-danger)', borderColor: 'var(--pr-danger)' }}
                                        onClick={() => {
                                            handleDeleteCategory(editingCategory.id);
                                            setShowCategoryModal(false);
                                        }}
                                    >
                                        🗑️ Supprimer cette catégorie
                                    </button>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button 
                                    className="products-btn products-btn-secondary" 
                                    onClick={() => setShowCategoryModal(false)}
                                >
                                    Annuler
                                </button>
                                <button 
                                    className="products-btn products-btn-primary" 
                                    onClick={handleSaveCategory}
                                    disabled={!categoryForm.name.trim()}
                                >
                                    {editingCategory ? "💾 Enregistrer" : "➕ Créer"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
